/**
 * Event page header — handoff §2. Desktop: the cover (EventCover) with the text column on top —
 * pill + mode line, sentence-case title sized to the column, category chips, trust line, when +
 * place, and the host row pinned to the bottom with a 48px round avatar. Mobile: the photo
 * cover alone, then the identity block in its own white card with the host as a #F5F5F5 pill.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Pill, MUTED } from '../surface';
import { SOFT } from '../surface/SurfaceStyleContext';
import { tintFor } from '../../utils/tints';
import { whenLabel, timeLabel } from '../../utils/surfaceFormat';
import EventCover from './EventCover';

export function whenLine(event) {
  if (!event?.dateTime) return 'DATE TBD';
  const start = whenLabel(event.dateTime);
  if (!event.endDateTime) return start;
  const a = new Date(event.dateTime); const b = new Date(event.endDateTime);
  if (a.toDateString() === b.toDateString()) return `${start} – ${timeLabel(event.endDateTime)}`;
  return `${start} – ${whenLabel(event.endDateTime)}`;
}

export function venueLine(event) {
  if (!event) return '';
  const place = event.venueName || (event.locationRevealed ? event.locationAddress : null) || event.locationCity || event.venueAddress;
  const city = event.locationCity && place !== event.locationCity ? event.locationCity : null;
  return [place, city].filter(Boolean).join(' · ');
}

/** Round avatar: the photo, or the initial on a tint keyed to the person. */
export function RoundAvatar({ person, size = 48, className = '' }) {
  const name = person?.displayName || '?';
  const initial = name.trim().charAt(0).toUpperCase() || '?';
  const tint = tintFor(person?.handle || person?.userId || name);
  return (
    <span
      className={`flex flex-shrink-0 items-center justify-center overflow-hidden rounded-full font-mono ${className}`}
      style={{ width: size, height: size, background: person?.avatarUrl ? '#F2F2F2' : tint, fontSize: Math.round(size * 0.38), fontWeight: 800 }}
      data-testid="round-avatar"
    >
      {person?.avatarUrl ? <img src={person.avatarUrl} alt="" className="block h-full w-full object-cover" referrerPolicy="no-referrer" /> : initial}
    </span>
  );
}

function HostRow({ event, label, mobile = false }) {
  const host = event?.host;
  if (!host) return null;
  const coHosts = Array.isArray(event.coHosts) ? event.coHosts : [];
  const handle = host.handle;
  const avatar = (h, size, key) => (
    h.handle
      ? <Link key={key} to={`/${h.handle}`} aria-label={`${h.displayName} — view Portal Page`} className="flex-shrink-0"><RoundAvatar person={h} size={size} /></Link>
      : <RoundAvatar key={key} person={h} size={size} />
  );
  const names = (
    <>
      {host.displayName}
      {coHosts.length > 0 && <span style={{ color: MUTED }}> + {coHosts.map((c) => c.displayName).join(', ')}</span>}
    </>
  );
  if (mobile) {
    return (
      <div className="flex items-center gap-[10px] rounded-full" style={{ padding: '6px 14px 6px 6px', background: SOFT.canvas }} data-testid="host-row">
        {avatar(host, 36, 'h')}
        <div className="flex min-w-0 flex-1 flex-col" style={{ gap: 2 }}>
          <span className="font-mono font-bold uppercase" style={{ fontSize: 8, letterSpacing: '0.16em', color: MUTED }}>{label}</span>
          <span className="truncate font-mono font-bold" style={{ fontSize: 12 }}>{names}</span>
        </div>
        {handle && <Link to={`/${handle}`} className="flex-shrink-0 font-mono font-bold uppercase" style={{ fontSize: 9, letterSpacing: '0.12em', color: MUTED }}>PORTAL PAGE →</Link>}
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-3" style={{ marginTop: 'auto' }} data-testid="host-row">
      <div className="flex items-center" style={{ gap: 6 }}>
        {avatar(host, 48, 'h')}
        {coHosts.map((c) => avatar(c, 36, c.userId || c.displayName))}
      </div>
      <div className="flex flex-col" style={{ gap: 3 }}>
        <span className="font-mono font-bold uppercase" style={{ fontSize: 9, letterSpacing: '0.16em', color: MUTED }}>{label}</span>
        <span className="font-mono font-bold" style={{ fontSize: 14 }}>{names}</span>
      </div>
      {handle && (
        <Link to={`/${handle}`} className="font-mono font-bold uppercase transition-colors duration-200 hover:text-black" style={{ marginLeft: 8, fontSize: 10, letterSpacing: '0.14em', color: MUTED }}>VIEW PORTAL PAGE →</Link>
      )}
    </div>
  );
}

function Trust({ trust, mobile = false }) {
  if (!trust) return null;
  return (
    <div className={`flex flex-wrap items-center ${mobile ? 'gap-2' : 'gap-3'}`} style={{ fontSize: mobile ? 11 : 12, letterSpacing: mobile ? '0.05em' : '0.06em' }} data-testid="trust-line">
      <span className="font-mono font-bold uppercase" style={{ color: trust.color }}>{trust.text}</span>
      {trust.note && <span className="font-mono uppercase" style={{ color: MUTED }}>{trust.note}</span>}
    </div>
  );
}

function Chips({ chips, gathering = null, mobile = false }) {
  if (!chips?.length && !gathering) return null;
  const chipStyle = { padding: mobile ? '5px 10px' : '7px 12px', fontSize: mobile ? 9 : 10, letterSpacing: '0.14em' };
  return (
    <div className="flex flex-wrap" style={{ gap: 6 }} data-testid="category-chips">
      {/* An agenda item of a club gathering says so, and the chip opens the whole gathering (Oct 20 2026). */}
      {gathering && (
        <Link to={`/gatherings/${gathering.id}`} className="rounded-full bg-black font-mono font-bold uppercase text-white transition-colors duration-200 hover:bg-[#00FFFF] hover:text-black" style={chipStyle} data-testid="gathering-chip">
          PART OF · {gathering.name}
        </Link>
      )}
      {(chips || []).map((c) => (
        <span key={c} className="rounded-full font-mono font-bold uppercase" style={{ background: SOFT.chip, ...chipStyle }}>{c}</span>
      ))}
    </div>
  );
}

/**
 * Desktop header. `photos` / `layout` / `split` feed the cover; `onOpenPhoto(index)` opens the
 * media viewer at that photo. `editable` + `onFocal` make the cover a framing editor (review step).
 */
export default function EventHeader({ event, model, photos = [], layout = 'auto', split = 60, onOpenPhoto, editable = false, onFocal, width, onRatio }) {
  return (
    <EventCover photos={photos} layout={layout} split={split} onOpenPhoto={onOpenPhoto} editable={editable} onFocal={onFocal} width={width} onRatio={onRatio} testId="event-header">
      {({ titleFs }) => (
        <>
          <div className="flex flex-wrap items-center" style={{ gap: 10 }}>
            <Pill tone={model.pill.tone}>{model.pill.label}</Pill>
            <span className="font-mono font-bold uppercase" style={{ fontSize: 11, letterSpacing: '0.18em', color: MUTED }}>{model.mode}</span>
          </div>
          <h1 data-pr-scramble className="font-mono normal-case" style={{ margin: 0, fontSize: titleFs, fontWeight: 700, lineHeight: 1.08, letterSpacing: '-0.01em', textWrap: 'pretty', overflowWrap: 'anywhere' }}>{event?.title}</h1>
          <Chips chips={model.chips} gathering={model.gathering} />
          <Trust trust={model.trust} />
          <div className="flex flex-col" style={{ gap: 6 }}>
            <span className="font-mono font-bold uppercase" style={{ fontSize: 18, letterSpacing: '0.04em' }}>{whenLine(event)}</span>
            {venueLine(event) && <span className="font-mono" style={{ fontSize: 13, color: SOFT.body }}>{venueLine(event)}</span>}
          </div>
          <HostRow event={event} label={model.hostLabel} />
        </>
      )}
    </EventCover>
  );
}

/** Mobile: the photo cover alone (300px, 380 stacked), full-bleed. Nothing when there are no photos. */
export function EventCoverMobile({ photos = [], layout = 'auto', split = 60, onOpenPhoto, editable = false, onFocal, width, onRatio }) {
  return <EventCover mobile photos={photos} layout={layout} split={split} onOpenPhoto={onOpenPhoto} editable={editable} onFocal={onFocal} width={width} onRatio={onRatio} testId="mobile-poster" />;
}

/** Mobile identity block under the cover (§2 Mobile): its own white card, host as a pill. */
export function EventIdentityMobile({ event, model }) {
  return (
    <div className="flex flex-col bg-white" style={{ padding: '18px 16px', gap: 12 }} data-testid="event-identity-mobile">
      <div className="flex flex-wrap items-center" style={{ gap: 8 }}>
        <Pill tone={model.pill.tone} size="sm">{model.pill.label}</Pill>
        <span className="font-mono font-bold uppercase" style={{ fontSize: 9, letterSpacing: '0.16em', color: MUTED }}>{model.mode}</span>
      </div>
      <h1 data-pr-scramble className="font-mono normal-case" style={{ margin: 0, fontSize: 26, fontWeight: 700, lineHeight: 1.12, textWrap: 'pretty', overflowWrap: 'anywhere' }}>{event?.title}</h1>
      <Chips chips={model.chips} gathering={model.gathering} mobile />
      <div className="flex flex-col" style={{ gap: 3 }}>
        <span className="font-mono font-bold uppercase" style={{ fontSize: 13, letterSpacing: '0.04em' }}>{whenLine(event)}</span>
        {venueLine(event) && <span className="font-mono" style={{ fontSize: 12, color: MUTED }}>{venueLine(event)}</span>}
      </div>
      <Trust trust={model.trust} mobile />
      <HostRow event={event} label={model.hostLabel} mobile />
    </div>
  );
}

export { HostRow };
